import type { CSSProperties, ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import type { To } from 'react-router';
import styled from 'styled-components';

import { FiatCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconChevronDoubleTop } from '@sorare/core/src/atoms/icons/system/iconChevronDoubleTop.ts';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandXXS } from '@sorare/core/src/atoms/typography';
import { NeonEdge } from '@sorare/core/src/atoms/ui/NeonEdge';
import { ShieldShape } from '@sorare/core/src/atoms/ui/ShieldShape';
import { CornerBadge } from '@sorare/core/src/components/CornerBadge';
import { MonetaryRewardDisclaimer } from '@sorare/core/src/components/jonum/MonetaryRewardDisclaimer';
import { ANY_SPORT_PLAY_BOARDS_PRO } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { useMarketHomeFilters } from '@sorare/marketplace/src/hooks/useMarketHomeFilters';
import type {
  ArenaPvpConfig,
  LeagueLaunch,
  ProHotStreakConfig,
} from '@sorare/marketplace/src/lib/newSeason';

import { ModeIcon } from '#src/components/ModeIcon/index.tsx';
import { getModeGradient, Mode } from '#src/lib/gameMode.ts';

const Tiles = styled(Horizontal)`
  width: 100%;
`;

const TileWrapper = styled.div`
  min-width: 0;
  flex: 1;
  border-radius: var(--double-unit);
  position: relative;
  @container ${tabletAndAbove} {
    max-width: 300px;
  }
`;

const Tile = styled.div`
  display: block;
  position: relative;
  width: 100%;
  border-radius: var(--double-unit);
  background: linear-gradient(
    180deg,
    rgba(var(--c-rgb-white), 0.1) 0%,
    rgba(var(--c-rgb-arena-primary), 0.08) 100%
  );

  overflow: hidden;
  border: 1px solid var(--c-nd-150);
`;

const StyledNeonEdge = styled(NeonEdge)`
  padding: var(--intermediate-unit);
  @media ${tabletAndAbove} {
    padding: var(--double-unit);
  }
`;

const BoostedWatermark = styled.div`
  position: absolute;
  top: 0;
  right: calc(var(--unit) * -3);
  & > svg {
    height: 112px;
  }
  color: var(--c-white);
  opacity: 0.1;
  pointer-events: none;
  overflow: hidden;
  border-radius: var(--double-unit);
`;

const BadgeCorner = styled.div`
  position: absolute;
  top: var(--unit);
  right: 0;
`;

const BaseAmount = styled(BrandXXS)`
  text-decoration: line-through;
`;

type RewardTileProps = {
  mode: Mode;
  config: ProHotStreakConfig | ArenaPvpConfig;
  label: ReactNode;
  to?: To;
};

const RewardTile = ({ mode, config, label, to }: RewardTileProps) => {
  const { usdAmount: rawUsdAmount, baseUsdAmount, boost } = config;
  const usdAmount =
    rawUsdAmount ??
    (baseUsdAmount != null && boost != null
      ? baseUsdAmount * boost
      : undefined);
  if (usdAmount === undefined) return null;

  const neonColor =
    boost !== undefined ? 'var(--c-arena-primary)' : 'var(--c-green-200)';

  return (
    <TileWrapper>
      <Tile {...(to ? { as: ButtonBase, to } : {})}>
        <StyledNeonEdge
          color={neonColor}
          showNeon="always"
          showHalo="always"
          spread="120%"
        >
          <Vertical gap={2}>
            <ShieldShape
              size="xs"
              orientation="up"
              borderColor={getModeGradient(mode)}
              icon={<ModeIcon mode={mode} size={2} />}
            />
            <Vertical gap={0.5}>
              <MonetaryRewardDisclaimer variant="monetary">
                <Horizontal
                  gap={0.5}
                  style={{
                    alignItems: 'baseline',
                  }}
                >
                  <BrandM
                    className="text-gradient-color"
                    style={
                      {
                        '--text-gradient-color':
                          mode === Mode.PRO
                            ? 'linear-gradient(180deg, #FFF 0%, #FFCBAE 100%)'
                            : 'linear-gradient(180deg, #FFF 0%, #F1AEFF 100%)',
                      } as CSSProperties
                    }
                  >
                    <FormattedNumber
                      value={usdAmount}
                      style="currency"
                      currency={FiatCurrency.USD}
                      currencyDisplay="narrowSymbol"
                      maximumFractionDigits={0}
                    />
                  </BrandM>
                  {baseUsdAmount !== undefined && (
                    <BaseAmount color="var(--c-nd-600)">
                      {/* Pre-boost reference of the same reward, already disclaimed above. */}
                      {/* oxlint-disable-next-line sorare/enforce-monetary-reward-disclaimer */}
                      <FormattedNumber
                        value={baseUsdAmount}
                        style="currency"
                        currency={FiatCurrency.USD}
                        currencyDisplay="narrowSymbol"
                        maximumFractionDigits={0}
                      />
                    </BaseAmount>
                  )}
                </Horizontal>
              </MonetaryRewardDisclaimer>
              <BrandXXS color="var(--c-nd-600)" uppercase>
                {label}
              </BrandXXS>
            </Vertical>
          </Vertical>
          {boost !== undefined && (
            <BoostedWatermark aria-hidden>
              <FontAwesomeIcon icon={iconChevronDoubleTop} />
            </BoostedWatermark>
          )}
        </StyledNeonEdge>
      </Tile>
      {boost !== undefined && (
        <BadgeCorner>
          <CornerBadge color="var(--c-gradient-greenYellow)">
            <BrandXXS bold uppercase as="span">
              <Horizontal gap={0.5} as="span">
                <FontAwesomeIcon icon={iconChevronDoubleTop} />
                <FormattedMessage
                  defaultMessage="{boost}x boost"
                  values={{ boost }}
                />
              </Horizontal>
            </BrandXXS>
          </CornerBadge>
        </BadgeCorner>
      )}
    </TileWrapper>
  );
};

type Props = {
  league: Pick<LeagueLaunch<unknown, unknown>, 'rewards'>;
};

export const LeagueRewards = ({ league }: Props) => {
  const { marketHomeFilters } = useMarketHomeFilters();
  const { generateSportContextPath } = useSportContext();
  const { isIosApp } = useIsMobileApp();
  const { rarity } = marketHomeFilters;

  const { proHotStreak: proHotStreakByRarity, arenaPvp: arenaPvpByRarity } =
    league.rewards ?? {};

  const proHotStreak = proHotStreakByRarity?.[rarity];
  const arenaPvp = arenaPvpByRarity?.[rarity];

  if (!proHotStreak && !arenaPvp) return null;

  return (
    <Tiles center>
      {!!proHotStreak && (
        <RewardTile
          mode={Mode.PRO}
          config={proHotStreak}
          to={
            isIosApp
              ? undefined
              : generateSportContextPath(ANY_SPORT_PLAY_BOARDS_PRO)
          }
          label={
            proHotStreak.levelNumber !== undefined ? (
              <FormattedMessage
                defaultMessage="Lvl {level} reward"
                values={{ level: proHotStreak.levelNumber }}
              />
            ) : (
              <FormattedMessage defaultMessage="Pro Hot Streak" />
            )
          }
        />
      )}
      {!!arenaPvp && (
        <RewardTile
          mode={Mode.ARENA_IN_SEASON}
          config={arenaPvp}
          label={
            arenaPvp.startGameweek !== undefined &&
            arenaPvp.endGameweek !== undefined ? (
              <FormattedMessage
                defaultMessage="GW {start}–{end}"
                values={{
                  start: arenaPvp.startGameweek,
                  end: arenaPvp.endGameweek,
                }}
              />
            ) : (
              <FormattedMessage defaultMessage="Arena PvP" />
            )
          }
        />
      )}
    </Tiles>
  );
};
